import * as React from 'react';
import { styled, useTheme } from '@mui/material/styles';
import {
  Box,
  Paper,
  Grid,
  TableContainer,
  Container,
  Tabs,
  Tab,
  useMediaQuery,
} from '@mui/material';
import DistributionPie from './DistributionPie';

const AntTabs = styled(Tabs)(() => ({
  border: '1px solid #e8e8e8',
  borderBottom: 'transparent',
  borderRadius: '8px 8px 0 0',
  background: '#fbfbfc',
  '& .MuiTabs-indicator': { backgroundColor: '#3a3a6f' },
  '& .MuiTabs-scrollButtons': {
    '&.Mui-disabled': { opacity: 0.3 },
  },
}));

const AntTab = styled((props) => <Tab disableRipple {...props} />)(({ theme }) => ({
  textTransform: 'none',
  minWidth: 0,
  [theme.breakpoints.up('sm')]: { minWidth: 0 },
  fontWeight: theme.typography.fontWeightRegular,
  marginRight: theme.spacing(1),
  color: 'rgba(0, 0, 0, 0.85)',
  fontFamily: [
    '-apple-system', 'BlinkMacSystemFont', '"Segoe UI"', 'Roboto',
    '"Helvetica Neue"', 'Arial', 'sans-serif', '"Apple Color Emoji"',
    '"Segoe UI Emoji"', '"Segoe UI Symbol"',
  ].join(','),
  '&:hover': { color: '#161b57', opacity: 1 },
  '&.Mui-selected': {
    color: 'primary.main',
    fontWeight: theme.typography.fontWeightMedium,
  },
  '&.Mui-focusVisible': { backgroundColor: '#d1eaff' },
}));

const Item = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'dark' ? 'rgb(10, 25, 41)' : '#fff',
  ...theme.typography.body2,
  height: '100%',
  color: theme.palette.text.secondary,
  boxShadow:
    theme.palette.mode === 'dark'
      ? 0
      : '0 0.5px 1px rgb(255 255 255 / 20%), 0 2px 3px rgb(255 255 255 / 10%)',
  border:
    theme.palette.mode === 'dark'
      ? '1px solid rgb(19, 47, 76)'
      : '1px solid #eae9ee',
  borderRadius: '7px',
  [theme.breakpoints.down('md')]: { borderRadius: '0 0 7px 7px' },
  '& .Item-Head': {
    background: '#fbfbfc',
    borderBottom: '1px solid #eae9ee',
    marginBottom: 0,
    padding: theme.spacing(1.5, 2, 1.5, 2),
    borderRadius: '7px 7px 0 0',
    '& a': { textDecoration: 'none' },
    '& .option-title': {
      fontWeight: 600,
      color: '#13113c',
      fontSize: '.875rem',
    },
  },
  '& .Item-Body': { padding: theme.spacing(1) },
}));

const hasData = (data) => data != null && data !== '';

const DistributionCard = React.memo(({ tab, txtBlock }) => (
  <Item
    component="div"
    className="py-2"
    sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}
  >
    <TableContainer>
      <DistributionPie
        data={tab.data}
        category={tab.category}
        value={tab.value}
        section={tab.section}
        txtBlock={txtBlock}
        title={tab.title}
      />
    </TableContainer>
  </Item>
));
DistributionCard.displayName = 'DistributionCard';

const Distribution = ({ fund_metrics }) => {
  const { sectors: sectorsPie, mktCapCat: mktCapPie, grp: indexPie, txtBlock } =
    fund_metrics || {};

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [tabIndex, setTabIndex] = React.useState(0);

  const handleTabChange = React.useCallback((event, newIndex) => {
    setTabIndex(newIndex);
  }, []);

  const visibleTabs = React.useMemo(
    () =>
      [
        { key: 'sector', data: sectorsPie, category: 'Sector', value: 'sector', section: 'sector', title: 'Sector Distribution' },
        { key: 'mktCap', data: mktCapPie, category: 'Category', value: 'mktCapCat', section: 'marketCap', title: 'Market Cap. Distribution' },
        { key: 'index', data: indexPie, category: 'Index', value: 'grp', section: 'index', title: 'Stocks belong to which Index?' },
      ].filter((tab) => hasData(tab.data)),
    [sectorsPie, mktCapPie, indexPie],
  );

  React.useEffect(() => {
    if (tabIndex > visibleTabs.length - 1) {
      setTabIndex(0);
    }
  }, [tabIndex, visibleTabs.length]);

  if (visibleTabs.length === 0) {
    return null;
  }

  const safeTabIndex = Math.min(tabIndex, visibleTabs.length - 1);

  return (
    <Container maxWidth="xl">
      <Box
        component="div"
        // className="row"
        sx={{
          mt: 1.5,
          position: 'relative',
          '& .MuiTabs-root .MuiTabs-scrollButtons': {
            position: 'absolute',
            py: 0,
            opacity: 1,
            zIndex: 99,
            height: '48px',
          },
          '& .MuiTabs-root .MuiTabs-scrollButtons.Mui-disabled': { display: 'none' },
          '& .MuiTabs-root .MuiTabScrollButton-root:first-of-type': {
            left: 1,
            pl: '5px',
            background: 'rgb(251 251 252)',
            justifyContent: 'flex-start',
            width: '37px',
            borderRadius: '7px 0 0 0',
          },
          '& .MuiTabs-root .MuiTabScrollButton-root:last-of-type': {
            right: 1,
            pr: '5px',
            background: 'rgb(251 251 252)',
            justifyContent: 'flex-end',
            width: '37px',
            borderRadius: '0 7px 0 0',
          },
        }}
      >
        {isMobile ? (
          <>
            <AntTabs
              value={safeTabIndex}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons
              allowScrollButtonsMobile
              aria-label="Fund distribution categories"
            >
              {visibleTabs.map((tab) => (
                <AntTab
                  key={tab.key}
                  label={tab.title}
                  id={`distribution-tab-${tab.key}`}
                  aria-controls={`distribution-tabpanel-${tab.key}`}
                />
              ))}
            </AntTabs>
            {visibleTabs.map((tab, index) => (
              <Box
                key={tab.key}
                role="tabpanel"
                hidden={safeTabIndex !== index}
                id={`distribution-tabpanel-${tab.key}`}
                aria-labelledby={`distribution-tab-${tab.key}`}
              >
                {safeTabIndex === index && (
                  <DistributionCard tab={tab} txtBlock={txtBlock} />
                )}
              </Box>
            ))}
          </>
        ) : (
          <Grid container spacing={1.2}>
            {visibleTabs.map((tab) => (
              <Grid key={tab.key} size={{ xs: 12, md: 4 }}>
                <DistributionCard tab={tab} txtBlock={txtBlock} />
              </Grid>
            ))}
          </Grid>
        )}
      </Box>
    </Container>
  );
};

export default Distribution;
